import { Component, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { MultiSubscriptionComponent, ValueOption } from '@ddv/common-components';
import { INCLUDE, DashboardFilter, ValueFilterOption, FilterCriteria } from '@ddv/models';
import { compareSortOptions } from '@ddv/utils';
import { OperatorPickerComponent } from '@hs/ui-core-additional-filters';
import { DropdownOption, DropdownOption as FilterListDropdownOption } from '@hs/ui-core-common';
import { ListComponent } from '@hs/ui-core-lists';

import { DashboardFiltersService } from '../../../services/dashboard-filters.service';

@Component({
    selector: 'app-filterable-value',
    templateUrl: 'filterable-value.component.html',
    styleUrls: ['filterable-value.component.scss'],
    imports: [
        ListComponent,
        OperatorPickerComponent,
    ],
})
export class FilterableValueComponent extends MultiSubscriptionComponent implements OnInit {
    @Input() selectedDashboardFilter: DashboardFilter | undefined;
    @Output() addFilteredValues = new EventEmitter<DashboardFilter>();

    selectedCount = 0;

    protected items: ValueFilterOption[] = [];
    protected searchOptions = {
        enabled: true,
        placeholder: 'Item search',
        addSearchValueAsOption: true,
    };
    protected selectedItems: ValueFilterOption[] = [];
    protected itemsContainBlanksOption = false;
    protected operatorsAvailable: DropdownOption[] = [
        { text: 'Include', value: '+' },
        { text: 'Exclude', value: '-' },
    ];
    protected operatorSelected: FilterListDropdownOption = { text: 'Include', value: '+' };

    private blanksSelected = false;
    private valueFilterOptions: ValueOption[] = [];

    constructor(public dashboardFiltersService: DashboardFiltersService) {
        super();
    }

    ngOnInit(): void {
        this.subscribeTo(this.dashboardFiltersService.valueFilterOptionsObserver, (options) => {
            this.setValueFilterOptions(options);

            this.setItemsAndSelectedItems();
        });

        this.dashboardFiltersService.updateValueOptions(this.selectedDashboardFilter?.name ?? '');
        this.mergeSelectedValues();

        this.setItemsAndSelectedItems();
    }

    protected onSelectedOperatorChange(operator: FilterListDropdownOption): void {
        if (!this.selectedDashboardFilter) {
            return;
        }

        this.selectedDashboardFilter.criteria = (operator.text.toUpperCase() as 'INCLUDE' | 'EXCLUDE') ?? 'INCLUDE';
        this.operatorSelected = {
            text: operator.text,
            value: this.selectedDashboardFilter.criteria === 'INCLUDE' ? '+' : '-',
        };

        if (this.selectedDashboardFilter?.values?.length) {
            this.addFilteredValues.emit(this.selectedDashboardFilter);
        }
    }

    protected onSelectedChoicesChange(choices: FilterListDropdownOption[]): void {
        if (!this.selectedDashboardFilter) {
            return;
        }

        this.selectedDashboardFilter.values = choices.map((ch) => ch.value);

        this.selectBlanksChange();
    }

    protected selectBlanksChange(selectBlanks?: boolean): void {
        if (!this.selectedDashboardFilter) {
            return;
        }

        this.blanksSelected = selectBlanks ?? this.blanksSelected;

        if (this.blanksSelected) {
            if (this.selectedItems[0]?.value !== '') {
                this.selectedItems.unshift({ text: 'Blanks', value: '', checked: false });
            }

            if (this.selectedDashboardFilter?.values?.[0] !== '') {
                this.selectedDashboardFilter.values?.unshift('');
            }
        } else {
            if (this.selectedItems[0]?.value === '') {
                this.selectedItems.splice(0, 1);
            }

            if (this.selectedDashboardFilter?.values?.[0] === '') {
                this.selectedDashboardFilter.values?.splice(0, 1);
            }
        }

        this.selectedDashboardFilter.criteria = this.operatorSelected.text.toUpperCase() as FilterCriteria ?? INCLUDE;
        this.dashboardFiltersService.updateTotalValueCount({ [this.selectedDashboardFilter.name ?? '']: this.valueFilterOptions.length });
        this.addFilteredValues.emit(this.selectedDashboardFilter);
    }

    private setValueFilterOptions(options: ValueFilterOption[]): void {
        const filteredOptions = options.filter((option) => {
            const text = option.text.toString();
            return !(text.startsWith('{') && text.endsWith('}')) && text.trim();
        });

        this.valueFilterOptions = filteredOptions
            .map((o) => ({
                texts: this.getTexts(o.value, o.text),
                value: o.value,
                checked: !!o.checked,
            }))
            .sort((a, b) => compareSortOptions(a.value, b.value));
    }

    private mergeSelectedValues(): void {
        if (!this.selectedDashboardFilter?.values?.length) {
            return;
        }

        const selectedValueFilterOptions = this.selectedDashboardFilter.values.map((v) => (
            { texts: this.getTexts(v, v != null ? v.toString() : undefined), value: v, checked: true }
        ));

        selectedValueFilterOptions.forEach((value) => {
            const selectedValueFilterOption = this.valueFilterOptions.find((val) => val.value === value.value);
            if (selectedValueFilterOption) {
                selectedValueFilterOption.checked = true;
            } else {
                this.valueFilterOptions.push(value);
            }

            this.selectedCount += 1;
        });

        this.valueFilterOptions.sort((a, b) => compareSortOptions(a.value, b.value));
    }

    private getTexts(value: string | number | boolean, text = ''): string[] {
        return value == null || (typeof value === 'string' && value.trim() === '') ? ['Blanks'] : [text];
    }

    private getTextForFilterOption(texts: string[]): string {
        return texts.join(' ').trim();
    }

    private setItemsAndSelectedItems(): void {
        this.items = this.valueFilterOptions.map((option) => (
            {
                text: this.getTextForFilterOption(option.texts),
                value: option.value,
                checked: option.checked,
            }
        ));
        this.selectedItems = this.items.filter((option) => option.checked);

        this.blanksSelected = !!this.selectedItems.filter((item) => item.value === '').length;

        this.itemsContainBlanksOption = this.items.some((item) => item.value === '');

        this.operatorSelected = this.operatorsAvailable.find((operator) =>
            operator.text.toUpperCase() === this.selectedDashboardFilter?.criteria) ?? this.operatorsAvailable[0];
    }
}
